import { onboardingLog } from '@/services/onboardingLog'
import { FirebaseError } from 'firebase/app'
import { deleteUser, onAuthStateChanged, reauthenticateWithPopup, signInWithPopup, signOut } from 'firebase/auth'
import { isAuthApiError, isAuthRetryableFetchError } from '@supabase/supabase-js'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { identify, resetAnalytics } from '@/services/analytics'
import { ApiError, api, withSignal, type RequestOptions } from '@/services/api'
import { syncAttribution } from '@/services/attribution'
import {
  enablePersistentAuth,
  firebaseAuth,
  googleAuthProvider,
  isFirebaseConfigured,
} from '@/services/firebase'
import { disableGoogleAutoSelect } from '@/services/googleIdentity'
import { authProvider } from '@/services/session'
import { isSupabaseConfigured, supabase } from '@/services/supabase'
import type { MeResponse } from '@/types/api'
import { useAppStore } from '@/stores/app'

function authErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) return 'Could not verify this session. Please try again.'
  if (error.code === 'auth/popup-closed-by-user') return 'Sign-in was cancelled.'
  if (error.code === 'auth/popup-blocked') return 'Allow popups to sign in with Google.'
  if (error.code === 'auth/operation-not-allowed') return 'Google sign-in is not enabled yet.'
  return 'Sign-in failed. Please try again.'
}

function idTokenErrorMessage(error: unknown): string {
  if (isAuthRetryableFetchError(error)) return 'Network error. Check your connection and try again.'
  if (isAuthApiError(error) && /provider.*(disabled|not enabled)/i.test(error.message)) {
    return 'Google sign-in is not enabled yet.'
  }
  if (isAuthApiError(error) && error.status >= 500) return 'Sign-in service is temporarily unavailable. Please try again.'
  return 'Google sign-in failed. Please try again.'
}

export type SignInMode = 'signin' | 'signup'
export type EmailSignupResult = 'confirmation_required' | 'signed_in' | null

function emailSignupErrorMessage(error: unknown): string {
  if (isAuthRetryableFetchError(error)) return 'Network error. Check your connection and try again.'
  if (isAuthApiError(error)) {
    if (error.code === 'weak_password') return 'Choose a stronger password with at least 8 characters.'
    if (error.status === 429) return 'Too many attempts. Please wait before trying again.'
    if (error.code === 'signup_disabled' || error.code === 'email_provider_disabled' || error.status === 404) {
      return 'Email registration is not available yet. Please continue with Google.'
    }
  }
  return 'Could not create your account. Try again or sign in if you already have an account.'
}

export const useAuthStore = defineStore('auth', () => {
  const app = useAppStore()
  const identity = ref<MeResponse | null>(null)
  const ready = ref(false)
  const sessionRejected = ref(false)
  const busy = ref(false)
  const error = ref<string | null>(null)
  const signInOpen = ref(false)
  const signInMode = ref<SignInMode>('signin')
  let initialization: Promise<void> | null = null
  let authEventRefresh: Promise<void> | null = null
  let wakeInitialization: (() => void) | undefined
  let accountRevision = 0
  let sessionOwner: string | null = null

  const isAuthenticated = computed(() => identity.value !== null)
  const displayName = computed(
    () => identity.value?.name ?? identity.value?.email ?? 'Signed-in user',
  )

  function openSignIn(mode: SignInMode = 'signin'): void {
    error.value = null
    signInMode.value = mode
    signInOpen.value = true
  }

  function closeSignIn(): void {
    signInOpen.value = false
  }

  function requireSignIn(): boolean {
    if (identity.value) return true
    openSignIn()
    return false
  }

  function clearIdentity(): void {
    error.value = null
    sessionRejected.value = false
    accountRevision++
    sessionOwner = null
    if (identity.value) resetAnalytics()
    identity.value = null
    app.$patch({
      credits: 0,
      hasActiveSubscription: false,
      canTopUp: false,
      subscriptionPaidUntil: null,
      nextCreditGrantAt: null,
      entitlementsKnown: false,
      entitlementsRefreshing: false,
    })
  }

  function selectSession(userId: string): void {
    const currentOwner = sessionOwner ?? identity.value?.user_id ?? null
    if (currentOwner !== userId || (identity.value && identity.value.user_id !== userId)) clearIdentity()
    sessionOwner = userId
  }

  function beginAccountRefresh(): number {
    const revision = ++accountRevision
    app.$patch({ canTopUp: false, entitlementsKnown: false, entitlementsRefreshing: true })
    onboardingLog('debug', '[UNI-133] account_refresh_started')
    return revision
  }

  function accountRefreshFailed(caught: unknown, revision: number): void {
    if (revision !== accountRevision) return
    if (caught instanceof ApiError && caught.status === 401) {
      clearIdentity()
      sessionRejected.value = true
    }
    else app.$patch({ canTopUp: false, entitlementsKnown: false, entitlementsRefreshing: false })
    error.value = caught instanceof ApiError && caught.status >= 500
      ? 'Account status is temporarily unavailable. Please retry.'
      : 'Your session could not be verified by the server.'
    onboardingLog('warn', '[UNI-133] account_refresh_failed', {
      status: caught instanceof ApiError ? caught.status : 'network',
    })
  }

  async function refreshIdentity(revision = beginAccountRefresh(), expectedOwner = sessionOwner, options: RequestOptions = {}): Promise<void> {
    const previousIdentity = identity.value?.user_id ?? null
    try {
      const snapshot = await api.me(options)
      options.signal?.throwIfAborted()
      if (revision !== accountRevision || (identity.value?.user_id ?? null) !== previousIdentity) return
      if (expectedOwner && snapshot.user_id !== expectedOwner) throw new ApiError('Account changed during refresh', 401)
      sessionOwner = snapshot.user_id
      sessionRejected.value = false
      identity.value = snapshot
      app.$patch({
        credits: snapshot.credits,
        hasActiveSubscription: snapshot.subscription_status === 'active',
        canTopUp: snapshot.can_top_up === true,
        subscriptionPaidUntil: snapshot.subscription_paid_until ?? null,
        nextCreditGrantAt: snapshot.next_credit_grant_at ?? null,
        entitlementsKnown: typeof snapshot.can_top_up === 'boolean',
        entitlementsRefreshing: false,
      })
      error.value = null
      identify(snapshot.user_id, { subscription_status: snapshot.subscription_status })
      void syncAttribution(snapshot.user_id)
      onboardingLog('debug', '[UNI-133] account_snapshot_applied', {
        canTopUp: app.canTopUp, known: app.entitlementsKnown,
      })
    } catch (caught) {
      if (options.signal?.aborted) return
      if ((identity.value?.user_id ?? null) !== previousIdentity) return
      accountRefreshFailed(caught, revision)
    }
  }

  async function settleAuthEvents(startup: Promise<void>): Promise<void> {
    // INITIAL_SESSION may supersede getSession or /me. Wait for the winning refresh,
    // without calling Supabase APIs while its onAuthStateChange lock is held.
    for (;;) {
      const current = authEventRefresh ?? startup
      onboardingLog('debug', '[UNI-141] auth_snapshot_wait')
      const changed = new Promise<void>((resolve) => { wakeInitialization = resolve })
      await Promise.race([current, changed])
      if (current === (authEventRefresh ?? startup)) break
    }
    wakeInitialization = undefined
  }

  function initialize(): Promise<void> {
    if (initialization) return initialization
    if (authProvider === 'supabase') {
      initialization = (async () => {
        onboardingLog('debug', '[UNI-141] auth_restore_started')
        if (!supabase) {
          ready.value = true
          error.value = isSupabaseConfigured ? null : 'Supabase is not configured.'
          return
        }
        supabase.auth.onAuthStateChange((_event, session) => {
          if (!session) {
            clearIdentity()
            authEventRefresh = Promise.resolve()
            wakeInitialization?.()
            return
          }
          selectSession(session.user.id)
          const revision = beginAccountRefresh()
          // Supabase calls this while holding its auth lock. Defer API work.
          authEventRefresh = new Promise<void>((resolve) => {
            setTimeout(() => {
              if (revision !== accountRevision) { resolve(); return }
              void refreshIdentity(revision, session.user.id).finally(resolve)
            }, 0)
          })
          wakeInitialization?.()
        })
        await settleAuthEvents(authEventRefresh ?? refresh())
        ready.value = true
        onboardingLog('debug', '[UNI-141] auth_restore_finished', { authenticated: isAuthenticated.value, unavailable: Boolean(error.value) })
      })()
      return initialization
    }
    initialization = new Promise((resolve) => {
      const auth = firebaseAuth
      if (!auth) {
        ready.value = true
        error.value = isFirebaseConfigured ? null : 'Firebase is not configured.'
        resolve()
        return
      }

      void enablePersistentAuth().catch(() => undefined).then(() => {
        let firstState = true
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            selectSession(user.uid)
            await refreshIdentity()
          } else clearIdentity()
          ready.value = true
          if (firstState) {
            firstState = false
            resolve()
          }
        })
      })
    })
    return initialization
  }

  async function signIn(): Promise<void> {
    if (!firebaseAuth) {
      error.value = 'Firebase is not configured.'
      return
    }
    busy.value = true
    error.value = null
    try {
      const result = await signInWithPopup(firebaseAuth, googleAuthProvider)
      selectSession(result.user.uid)
      await refreshIdentity()
    } catch (caught) {
      error.value = authErrorMessage(caught)
    } finally {
      busy.value = false
    }
  }

  async function signInWithGoogleIdToken(token: string, nonce: string): Promise<void> {
    if (!supabase) {
      error.value = 'Supabase is not configured.'
      return
    }
    busy.value = true
    error.value = null
    try {
      const { data, error: signInError } = await supabase.auth.signInWithIdToken({ provider: 'google', token, nonce })
      if (signInError) throw signInError
      if (data.user) selectSession(data.user.id)
      await refreshIdentity()
    } catch (caught) {
      error.value = idTokenErrorMessage(caught)
    } finally {
      busy.value = false
    }
  }

  async function signInWithPassword(email: string, password: string): Promise<void> {
    if (authProvider !== 'supabase' || !supabase) {
      error.value = 'Supabase is not configured.'
      return
    }
    busy.value = true
    error.value = null
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) throw signInError
      if (data.user) selectSession(data.user.id)
      await refreshIdentity()
    } catch {
      error.value = 'Invalid email or password. Please try again.'
    } finally {
      busy.value = false
    }
  }

  async function signUpWithPassword(email: string, password: string): Promise<EmailSignupResult> {
    if (busy.value) return null
    if (authProvider !== 'supabase' || !supabase) {
      error.value = 'Supabase is not configured.'
      return null
    }
    busy.value = true
    error.value = null
    try {
      const { data, error: signupError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { emailRedirectTo: new URL('/registration', window.location.origin).href },
      })
      if (signupError) throw signupError
      // User без session ещё не подтверждает вход или владение email.
      if (!data.session) return 'confirmation_required'
      selectSession(data.session.user.id)
      await refreshIdentity()
      return isAuthenticated.value ? 'signed_in' : null
    } catch (caught) {
      error.value = emailSignupErrorMessage(caught)
      onboardingLog('warn', 'email_signup_failed', {
        status: isAuthApiError(caught) ? caught.status : 'network',
      })
      return null
    } finally {
      busy.value = false
    }
  }

  async function logOut(): Promise<void> {
    if (authProvider === 'supabase') {
      if (!supabase) return
      busy.value = true
      error.value = null
      beginAccountRefresh()
      try {
        const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' })
        if (signOutError) throw signOutError
        disableGoogleAutoSelect()
        clearIdentity()
      } catch {
        app.entitlementsRefreshing = false
        error.value = 'Could not sign out. Please try again.'
      } finally {
        busy.value = false
      }
      return
    }
    if (!firebaseAuth) return
    busy.value = true
    error.value = null
    beginAccountRefresh()
    try {
      await signOut(firebaseAuth)
      clearIdentity()
    } finally {
      app.entitlementsRefreshing = false
      busy.value = false
    }
  }

  async function deleteAccount(): Promise<void> {
    if (authProvider === 'supabase') {
      if (!supabase) throw new Error('Sign in before deleting your account.')
      busy.value = true
      error.value = null
      beginAccountRefresh()
      try {
        await api.deleteAccount()
        clearIdentity()
        await supabase.auth.signOut({ scope: 'local' })
      } catch (caught) {
        error.value = caught instanceof ApiError ? caught.message : 'Could not delete your account.'
        throw caught
      } finally {
        app.entitlementsRefreshing = false
        busy.value = false
      }
      return
    }
    const user = firebaseAuth?.currentUser
    if (!user) throw new Error('Sign in before deleting your account.')
    busy.value = true
    error.value = null
    beginAccountRefresh()
    let serverDeleted = false
    try {
      await reauthenticateWithPopup(user, googleAuthProvider)
      await api.deleteAccount()
      serverDeleted = true
      clearIdentity()
      await deleteUser(user)
    } catch (caught) {
      error.value = serverDeleted
        ? 'Your UIG data was removed, but Firebase sign-in could not be deleted. Please retry.'
        : caught instanceof ApiError ? caught.message : authErrorMessage(caught)
      throw caught
    } finally {
      app.entitlementsRefreshing = false
      busy.value = false
    }
  }

  async function refresh(options: RequestOptions = {}): Promise<void> {
    let revision = beginAccountRefresh()
    try {
      let userId: string | undefined
      if (authProvider === 'supabase') {
        const { data, error: sessionError } = await withSignal(
          supabase?.auth.getSession() ?? Promise.resolve({ data: { session: null }, error: null }), options.signal,
        )
        options.signal?.throwIfAborted()
        if (revision !== accountRevision) return
        if (sessionError) throw sessionError
        userId = data?.session?.user.id
      } else userId = firebaseAuth?.currentUser?.uid
      if (!userId) {
        clearIdentity()
        return
      }
      if (sessionOwner !== userId || (identity.value && identity.value.user_id !== userId)) {
        selectSession(userId)
        revision = beginAccountRefresh()
      }
      await refreshIdentity(revision, userId, options)
    } catch (caught) {
      if (options.signal?.aborted) return
      accountRefreshFailed(caught, revision)
    }
  }

  return {
    identity,
    ready,
    sessionRejected,
    busy,
    error,
    signInOpen,
    signInMode,
    isAuthenticated,
    displayName,
    initialize,
    openSignIn,
    closeSignIn,
    requireSignIn,
    signIn,
    signInWithGoogleIdToken,
    signInWithPassword,
    signUpWithPassword,
    logOut,
    deleteAccount,
    refresh,
  }
})
